'use client';

import { useCallback, useState } from 'react';
import { useAuthService } from './useAuthService';

type UseEmailVerificationParams = {
  phone: string;
  otp: string;
};

type UseEmailVerificationReturn = {
  remainingAttempts: number | undefined;
  isVerifying: boolean;
  isVerified: boolean;
  isFailed: boolean;
  verifyEmail: (selectedEmail: string) => Promise<boolean>;
  reset: () => void;
};

/**
 * Hook for verifying email ownership of existing users
 *
 * @description
 * Used when `candidateEmails` is returned after OTP verification.
 * When user selects their actual email, account is linked and login completes.
 *
 * ## Prerequisites
 * - After OTP verification in `useOTPAuth`
 * - `userType` is `customer` or `subscriber`
 * - `candidateEmails` exists
 *
 * ## candidateEmails Composition
 * - 1 real email + 2 decoy emails = 3 total
 * - User must select their own email for security verification
 *
 * ## Verification Results
 * - Correct selection → `isVerified: true` + WordPress session cookie set
 * - Wrong selection → `remainingAttempts` decreases (max 3 attempts)
 * - Attempts exhausted → `isFailed: true`
 *
 * @example
 * ```tsx
 * const { candidateEmails, otpCode, phoneNumber } = useOTPAuth({ phoneNumber: '+821012345678' });
 *
 * // Use only when candidateEmails exists
 * const { verifyEmail, isVerified, remainingAttempts } = useEmailVerification({
 *   phone: phoneNumber,
 *   otp: otpCode!,
 * });
 *
 * // Display candidateEmails in UI and let user select
 * const success = await verifyEmail(selectedEmail);
 *
 * if (isVerified) {
 *   // Login complete
 * }
 * ```
 */
export const useEmailVerification = ({ phone, otp }: UseEmailVerificationParams): UseEmailVerificationReturn => {
  const { useVerifyEmail } = useAuthService();
  const { mutateAsync: verifyEmailMutation, isPending: isVerifying } = useVerifyEmail();

  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>(undefined);
  const [isVerified, setIsVerified] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const verifyEmail = useCallback(
    async (selectedEmail: string): Promise<boolean> => {
      if (!phone || !otp) {
        return false;
      }

      const result = await verifyEmailMutation({ phone, otp, selectedEmail });

      if (result.success && result.data.emailVerified) {
        setIsVerified(true);
        return true;
      }

      setRemainingAttempts(result.data.remainingAttempts);

      if (result.data.remainingAttempts === 0) {
        setIsFailed(true);
      }

      return false;
    },
    [phone, otp, verifyEmailMutation],
  );

  const reset = useCallback(() => {
    setRemainingAttempts(undefined);
    setIsVerified(false);
    setIsFailed(false);
  }, []);

  return {
    remainingAttempts,
    isVerifying,
    isVerified,
    isFailed,
    verifyEmail,
    reset,
  };
};
